import type { AutoCombineEntryV2 } from "@/shared/cache";
import { Badge } from "@/v2/components/ui/badge";
import {
	useSmartTimer,
	useSmartTimerDisplaySeconds,
} from "@/v2/hooks/useSmartTimer";

function StaticUnits({ value }: { value: number }) {
	return (
		<div className="flex w-full justify-center">
			<Badge className="min-w-[50px] justify-center rounded-[7px] bg-foreground px-2.5 py-1.5 text-[0.8125rem] font-[525] leading-none tabular-nums text-background">
				{value.toFixed(1)}
			</Badge>
		</div>
	);
}

/**
 * The running group's live units. Subscribes to the timer display context in
 * isolation, so only this cell re-renders as time accrues — not the table. Shown
 * as the same `.1` units badge as any other row (updates each billing increment).
 */
// Standard 6-minute billing increment (0.1 units). The live figure rounds up to
// this grid — like the settled billable value — so it matches the idle units
// instead of dipping below them (raw hours would round to the nearest tenth).
const BILLING_GRID_SECONDS = 360;

function LiveUnits() {
	const seconds = useSmartTimerDisplaySeconds();
	// Floor at the .1 minimum so a just-started timer reads as a real 0.1 entry
	// and accrues from there, rather than sitting at 0.0.
	const units = Math.max(0.1, Math.ceil(seconds / BILLING_GRID_SECONDS) / 10);
	return <StaticUnits value={units} />;
}

/**
 * Units cell for the auto-combine table. Renders a live clock for the group that
 * owns the running (or winding-down) smart timer, and the settled units badge
 * otherwise. Reading the active id here (not per-second state) means every row
 * re-renders only on start/pause, while the tick stays scoped to LiveUnits.
 */
export function RunningUnitsCell({
	entry,
	value,
}: {
	entry: AutoCombineEntryV2;
	value: number;
}) {
	const { activeEntryGroupId, pausingEntryGroupId } = useSmartTimer();
	const runningId = activeEntryGroupId ?? pausingEntryGroupId;
	return runningId === entry.id ? <LiveUnits /> : <StaticUnits value={value} />;
}
